import React, { useEffect, useState } from "react";
import { toast } from "sonner";

import { Button } from "@/app/components/ui/button.jsx";
import {
	DropdownMenu,
	DropdownMenuContent,
	DropdownMenuItem,
	DropdownMenuTrigger,
} from "@/app/components/ui/dropdown-menu.jsx";
import { Input } from "@/app/components/ui/input.jsx";
import { CheckIcon } from "@/app/components/ui/interface.jsx";
import { Label } from "@/app/components/ui/label.jsx";
import { Textarea } from "@/app/components/ui/textarea.jsx";
import { JobApplicationState, JobApplicationStatus } from "@/app/handlers/enums.js";
import { useUpdateApplicationStage, useUpdateApplicationStatus } from "@/app/services/create-resume/queries.js";

function JobStatusForm({ applicationId, currentStatus, onSuccess, onCancel }) {
	const [selectedStatus, setSelectedStatus] = useState(JobApplicationStatus.APPLIED);
	const [notes, setNotes] = useState("");
	const [dueDate, setDueDate] = useState("");
	const [selectedState, setSelectedState] = useState(JobApplicationState.NOT_STARTED);

	const [mode, setMode] = useState("create");

	useEffect(() => {
		if (currentStatus) {
			setSelectedStatus(currentStatus.status);
			setNotes(currentStatus.notes || "");
			setDueDate(currentStatus.dueDate ? new Date(currentStatus.dueDate).toISOString().slice(0, 16) : "");
			setSelectedState(currentStatus.state || JobApplicationState.NOT_STARTED);
			setMode("update");
		} else {
			setSelectedStatus(JobApplicationStatus.APPLIED);
			setNotes("");
			setDueDate("");
			setSelectedState(JobApplicationState.NOT_STARTED);
			setMode("create");
		}
	}, [currentStatus]);

	const updateApplicationStatusMutation = useUpdateApplicationStatus({
		onSuccess: () => {
			toast.success("Status updated successfully");
			if (onSuccess) onSuccess();
			onCancel();
		},
	});

	const updateStageStatusMutation = useUpdateApplicationStage({
		onSuccess: () => {
			toast.success("Stage updated successfully");
			if (onSuccess) onSuccess();
			onCancel();
		},
	});

	const handleSubmit = async (e) => {
		e.preventDefault();

		if (!applicationId) {
			toast.error("Job ID is required");
			return;
		}

		try {
			if (mode === "update" && currentStatus) {
				await updateStageStatusMutation.mutateAsync({
					applicationId,
					stageId: currentStatus._id,
					payload: {
						status: selectedStatus,
						notes: notes.trim() || undefined,
						dueDate: dueDate ? new Date(dueDate) : undefined,
						state: selectedState,
					},
				});
			} else {
				await updateApplicationStatusMutation.mutateAsync({
					applicationId,
					status: selectedStatus,
					notes: notes.trim() || undefined,
					dueDate: dueDate ? new Date(dueDate) : undefined,
					state: selectedState,
				});
			}
		} catch (error) {
			toast.error(error.message);
		}
	};

	return (
		<form onSubmit={handleSubmit} className="space-y-4">
			<div className="grid grid-cols-1 md:grid-cols-2 gap-4">
				<div className="space-y-2">
					<Label htmlFor="status">Status</Label>
					<DropdownMenu>
						<DropdownMenuTrigger asChild>
							<Button variant="outline" className="w-full justify-between">
								{selectedStatus.replace(/_/g, " ")}
								<span className="text-xs opacity-50">▼</span>
							</Button>
						</DropdownMenuTrigger>
						<DropdownMenuContent className="w-full" style={{ backgroundColor: "white" }}>
							{Object.values(JobApplicationStatus).map((status) => (
								<DropdownMenuItem
									key={status}
									style={{ cursor: "pointer" }}
									onClick={() => setSelectedStatus(status)}
									className={selectedStatus === status ? "bg-blue-50" : ""}
								>
									{status.replace(/_/g, " ")}
								</DropdownMenuItem>
							))}
						</DropdownMenuContent>
					</DropdownMenu>
				</div>

				<div className="space-y-2">
					<Label htmlFor="state">State</Label>
					<DropdownMenu>
						<DropdownMenuTrigger asChild>
							<Button variant="outline" className="w-full justify-between">
								{selectedState}
								<span className="text-xs opacity-50">▼</span>
							</Button>
						</DropdownMenuTrigger>
						<DropdownMenuContent className="w-full" style={{ backgroundColor: "white" }}>
							{Object.values(JobApplicationState).map((state) => (
								<DropdownMenuItem
									key={state}
									style={{ cursor: "pointer" }}
									onClick={() => setSelectedState(state)}
									className={selectedState === state ? "bg-blue-50" : ""}
								>
									{state}
								</DropdownMenuItem>
							))}
						</DropdownMenuContent>
					</DropdownMenu>
				</div>
			</div>

			<div className="space-y-2">
				<Label htmlFor="dueDate">Due Date and Time (Optional)</Label>
				<Input
					type="datetime-local"
					id="dueDate"
					className="w-full"
					value={dueDate}
					onChange={(e) => {
						const date = e.target.value;
						setDueDate(date);
					}}
				/>
			</div>

			<div className="space-y-2">
				<Label htmlFor="notes">Notes (Optional)</Label>
				<Textarea
					id="notes"
					placeholder="Add any notes about this status update..."
					value={notes}
					onChange={(e) => setNotes(e.target.value)}
					rows={3}
				/>
			</div>

			<div className="flex justify-end gap-2">
				<Button type="button" variant="outline" onClick={onCancel} disabled={updateApplicationStatusMutation.isPending}>
					Cancel
				</Button>
				<Button type="submit" disabled={updateApplicationStatusMutation.isPending} className="flex items-center gap-1">
					{updateApplicationStatusMutation.isPending ? (
						<>{mode === "update" ? "Updating..." : "Adding..."}</>
					) : (
						<>
							<CheckIcon size={16} />
							{mode === "update" ? "Update Stage" : "Add Stage"}
						</>
					)}
				</Button>
			</div>
		</form>
	);
}

export default JobStatusForm;
